import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Collapse } from "reactstrap";
import { FaServer, FaPlusCircle, FaCog, FaSearch, FaTimes, FaChevronDown, FaChevronUp } from "react-icons/fa";

import { enAdminTap } from "../../lib/ConstCommand";
import { getIsMobile } from "../../lib/func/ExportFunction";
import ConstData from "../../lib/ConstData";
import { defaultLocalServerFilters, matchesLocalServerFilters } from "../../lib/localServerFilters";

import WebAddLocalServer from "./WebAddLocalServer";
import WebLocalServerList from "./list/WebLocalServerList";
import WebReportTokenReset from "./list/WebReportTokenReset";
import WebConfigSearchBoxExceptUser from "./menu/WebConfigSearchBoxExceptUser";
import WebPagination from "../etc/WebPagination";
import Alerts from "../etc/list/Alerts";

const isMobile = getIsMobile();

// 20261007 로컬 서버 검색어 일치 여부 (서버 이름/인증 코드/IP/사이트 이름/순번, 대소문자 무시 부분 일치)
const isMatchLocalServerKeyword = (info, keyword) => {
    const lowerKeyword = (keyword || "").trim().toLowerCase();
    if (lowerKeyword === "") return true;

    return [info.localServerName, info.authCode, info.ipAddress, info.localSiteName, info.localServerSeq]
        .some(value => value !== undefined && value !== null && String(value).toLowerCase().includes(lowerKeyword));
};

const WebLocalServerConfig = ({ selectedSubDomainInfo, list_localSite, list_configLocalServer, list_configSubDomain, list_localServerType, alertKind, alertMessage, localServerAlertEventId, localServerAlertTarget, lastCreatedLocalServerSeq, initialListState, handleSelectedFunc }) => {
    const { t } = useTranslation();

    // 한 페이지에 보여줄 개수 (10/20/30/50/100 선택 가능)
    // 20260819 이력 화면에서 돌아온 경우 기존 목록의 페이지와 조회 조건을 복원한다.
    const [limit, setLimit] = useState(initialListState && initialListState.limit ? initialListState.limit : 10);
    const [page, setPage] = useState(initialListState && initialListState.page ? initialListState.page : 1);
    const [offset, setOffset] = useState((page - 1) * limit);
    const [blockNum, setBlockNum] = useState(initialListState && initialListState.blockNum ? initialListState.blockNum : 0);

    // 컴포넌트 초기 로드 여부를 추적하는 상태 추가
    const [isFirstLoad, setIsFirstLoad] = useState(true);

    const [isAddLocalServerOK, setIsAddLocalServerOK] = useState(false);
    const [isAddLocalServerOpen, setIsAddLocalServerOpen] = useState(false);
    const [isLocalServerTypeOpen, setIsLocalServerTypeOpen] = useState(false);
    const [draftLocalServerTypes, setDraftLocalServerTypes] = useState([]);
    const [typeAlert, setTypeAlert] = useState({ kind: ConstData.ALERT_KIND.NONE, message: "" });
    const [addLocalServerResetEventId, setAddLocalServerResetEventId] = useState(0);

    // 260804 로컬 사이트 필터 / 정렬 옵션 (요구사항 7) - 전체 클라이언트 처리라 별도 API 호출 없음
    const [siteFilterSeq, setSiteFilterSeq] = useState(initialListState && initialListState.siteFilterSeq !== undefined ? initialListState.siteFilterSeq : -1); // -1: 전체
    const [sortBy, setSortBy] = useState(initialListState && initialListState.sortBy ? initialListState.sortBy : "site"); // "site": 사이트별 그룹, "seq": 서버 번호(Seq)순
    // 20261007 받아온 전체 목록 안에서 검색하는 검색어 (이력 화면에서 돌아오면 복원)
    const [searchKeyword, setSearchKeyword] = useState(initialListState && initialListState.searchKeyword ? initialListState.searchKeyword : "");
    // 20261023 초기화로 필터 결과에서 서버가 빠져도 완료 안내를 유지한다.
    const [tokenResetServerSeq, setTokenResetServerSeq] = useState(null);
    // 20261027 검색 결과에서 행이 사라져도 초기화 대상과 확인창을 유지한다.
    const [tokenResetTarget, setTokenResetTarget] = useState(null);
    // 20261023 기존 즉시 검색과 별개로 상태 조건을 AND 결합하고 이력 복귀 시 복원한다.
    const [statusFilters, setStatusFilters] = useState({ ...defaultLocalServerFilters, ...(initialListState && initialListState.statusFilters) });
    // 20261027 스마트폰은 필터를 접어 목록 공간을 확보하고 선택 조건은 그대로 유지한다.
    const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(!isMobile);
    const changeStatusFilters = (changes) => {
        setStatusFilters(prev => ({ ...prev, ...changes }));
        setPage(1);
        setBlockNum(0);
    };

    // 20261023 선택 상자와 현재 조건 표시가 같은 이름·값을 사용하도록 정의를 공유한다.
    const statusFilterDefinitions = [
        ["update", "자동_업데이트", [["all", "전체"], ["allowed", "필터_허용"], ["blocked", "필터_차단"]]],
        ["token", "보고_토큰_상태", [["all", "전체"], ["unissued", "보고_토큰_unissued"], ["unconfirmed", "보고_토큰_unconfirmed"], ["verified", "보고_토큰_verified"], ["recovery", "필터_복구_필요_전체"], ["missing", "보고_토큰_missing"], ["invalid", "보고_토큰_invalid"], ["mismatch", "보고_토큰_mismatch"], ["reset", "보고_토큰_reset"], ["unknown", "보고_토큰_unknown"]]],
        ["connection", "필터_접속_상태", [["all", "전체"], ["Y", "필터_로그인"], ["N", "필터_로그아웃"]]],
        ["status", "마지막_업데이트_상태", [["all", "전체"], ["SUCCESS", "필터_성공"], ["FAILED", "필터_실패"], ["PARTIAL", "필터_부분_완료"], ["progress", "필터_진행_중"], ["unknown", "필터_정보_없음"]]],
        ["mqtt", "MQTT_사용_가능", [["all", "전체"], ["Y", "필터_지원"], ["N", "필터_미지원"], ["unknown", "필터_확인_안됨"]]],
        ["type", "로컬_서버_타입", [["all", "전체"], ...Array.from(new Map([...(list_localServerType || []).map(info => [info.localServerType, `서버_타입_${info.localServerType}`]), ...(list_configLocalServer || []).map(info => [info.localServerType || "UNKNOWN", `서버_타입_${info.localServerType || "UNKNOWN"}`])]).entries())]],
        // 20261027 지원 분류와 실제 수집된 개별 버전을 같은 조회 조건에서 선택한다.
        ["rabbitmq", "RabbitMQ_버전_필터", [["all", "전체"], ["supported", "RabbitMQ_버전_supported"], ["unsupported", "RabbitMQ_버전_unsupported"], ["unknown", "RabbitMQ_버전_unknown"],
            ...Array.from(new Set((list_configLocalServer || []).filter(info => info.rabbitmqInfoEnabled && typeof info.rabbitmqVersion === "string" && info.rabbitmqVersion)
                .map(info => info.rabbitmqVersion))).sort((a, b) => b.localeCompare(a, undefined, { numeric: true })).map(version => [`version:${version}`, version])]]
    ];
    const filterOptionLabel = (value, text) => {
        // 20261027 수집된 버전 숫자는 번역 키가 아니므로 선택 상자와 조건 배지에 그대로 표시한다.
        if (typeof value === "string" && value.startsWith("version:")) return text;
        return t(text, { ns: "admin", defaultValue: text.startsWith("서버_타입_") ? value : text });
    };
    // 20261023 미리보기의 부드러운 테두리는 유지하고 관리자 화면에 맞춰 글자를 작게 표시한다.
    // 20261026 초기화 시 테두리 색상이 제거되지 않도록 border 축약형과 borderColor 혼용을 피한다.
    const quickFilterButtonStyle = { background: '#fff', color: '#2869e8', borderWidth: '1px', borderStyle: 'solid', borderColor: '#dfe5ee', borderRadius: '5px', padding: '8px 12px', fontSize: '0.8rem', lineHeight: 1.5, cursor: 'pointer', margin: 0, boxShadow: 'none' };
    const isQuickFilterSelected = (preset) => Object.keys(defaultLocalServerFilters).every(key => statusFilters[key] === (preset[key] || "all"));
    const quickFilterStyle = (preset) => ({ ...quickFilterButtonStyle, ...(isQuickFilterSelected(preset) ? { background: '#edf4ff', borderColor: '#2869e8' } : {}) });

    useEffect(() => {
        // 20260814 생성·상세·타입 관리 화면이 공유할 서버 타입 기준정보를 한 번 조회한다.
        handleSelectedFunc({ selectedFunc: "GET_LOCAL_SERVER_TYPES" });
        // console.log("WebLocalServerConfig visible ... isMobile => ", isMobile);
        // console.log("list_configSubDomain => ", list_configSubDomain);

        if (isMobile) {
            if (list_configSubDomain && list_configSubDomain.length <= 0) {
                console.log("have to request sub domain manage list !!!");

                handleSelectedFunc({ selectedFunc: "GET_MANAGE_INFO" });
            }
        }

        return () => {
            // console.log("WebLocalServerConfig invisible");

            // 컴포넌트가 언마운트될 때 초기화
            setIsFirstLoad(true);
        }
    }, []);

    useEffect(() => {
        let offset = (page - 1) * limit;
        setOffset(offset);
    }, [page, limit]);

    useEffect(() => {
        setIsAddLocalServerOK(true);

        // 초기 로드 시에만 페이지를 1로 설정하고, 이후에는 현재 페이지 유지
        if (isFirstLoad) {
            setPage(initialListState && initialListState.page ? initialListState.page : 1);
            setOffset((page - 1) * limit);
            setIsFirstLoad(false);
        }

        /* return () => {
            setPage(1);
            setOffset((page - 1) * limit);
        } */
    }, [list_configLocalServer, page, isFirstLoad]);

    const handleLocalServerSelectedFunc = (selectedInfo) => {
        if (selectedInfo.selectedFunc === "LOCAL_SERVER_TOKEN_RESET_OPEN") {
            if (!tokenResetTarget) setTokenResetTarget({ ...selectedInfo.data });
            return;
        }
        if (selectedInfo.selectedFunc === "LOCAL_SERVER_TOKEN_RESET_DONE") setTokenResetServerSeq(selectedInfo.data.localServerSeq);
        if (selectedInfo.selectedFunc === "LOCAL_SERVER_HISTORY") {
            // 20260819 선택 서버의 이력 화면에 현재 목록 위치와 조회 조건을 함께 전달한다.
            handleSelectedFunc({
                ...selectedInfo,
                data: {
                    ...selectedInfo.data,
                    // 20261007 검색어도 함께 전달해 돌아왔을 때 복원
                    localServerListState: { page, limit, blockNum, siteFilterSeq, sortBy, searchKeyword, statusFilters }
                }
            });
            return;
        }
        handleSelectedFunc(selectedInfo);
    };

    const onChangeIsAddLocalServerOK = () => {
        setIsAddLocalServerOK(!isAddLocalServerOK);
    }

    // 20260813 등록 패널을 닫으면 입력 컴포넌트를 정리해 미저장 값을 폐기한다.
    const toggleAddLocalServer = () => setIsAddLocalServerOpen(!isAddLocalServerOpen);
    const resetAddLocalServer = () => setAddLocalServerResetEventId(prev => prev + 1);

    useEffect(() => {
        // 20260814 서버에서 조회된 타입 사용 상태를 패널의 임시 편집값으로 동기화한다.
        setDraftLocalServerTypes((list_localServerType || []).map(info => ({ ...info })));
    }, [list_localServerType]);

    const toggleLocalServerType = () => {
        if (isLocalServerTypeOpen) {
            // 20260814 저장하지 않고 닫으면 마지막 저장 상태로 되돌린다.
            setDraftLocalServerTypes((list_localServerType || []).map(info => ({ ...info })));
            setTypeAlert({ kind: ConstData.ALERT_KIND.NONE, message: "" });
        }
        setIsLocalServerTypeOpen(!isLocalServerTypeOpen);
    };

    const onToggleLocalServerTypeUse = (localServerType) => {
        setDraftLocalServerTypes(prev => prev.map(info => info.localServerType === localServerType ? { ...info, useYn: info.useYn === "Y" ? "N" : "Y" } : info));
    };

    const hasLocalServerTypeChanges = draftLocalServerTypes.some(draft => {
        const saved = (list_localServerType || []).find(info => info.localServerType === draft.localServerType);
        return saved && saved.useYn !== draft.useYn;
    });

    const onSaveLocalServerTypeUse = () => {
        // 20260814 체크 버튼을 누를 때 변경된 타입을 하나의 일괄 저장 요청으로 전달한다.
        const listLocalServerType = draftLocalServerTypes.reduce((list, draft) => {
            const saved = (list_localServerType || []).find(info => info.localServerType === draft.localServerType);
            if (saved && saved.useYn !== draft.useYn) {
                list.push({ localServerType: draft.localServerType, useYN: draft.useYn });
            }
            return list;
        }, []);

        if (listLocalServerType.length > 0) {
            handleSelectedFunc({ selectedFunc: "UPDATE_LOCAL_SERVER_TYPES_USE", data: { listLocalServerType } });
        }
    };

    // 260804 특정 로컬 사이트를 선택하면 이미 그 사이트 서버만 보이므로 "사이트별 그룹" 정렬은 의미가 없어 서버 번호순으로 고정
    useEffect(() => {
        if (siteFilterSeq !== -1 && sortBy !== "seq") {
            setSortBy("seq");
        }
    }, [siteFilterSeq]);

    const onChangeSiteFilter = (e) => {
        setSiteFilterSeq(Number(e.target.value));
        setPage(1);
    }

    const onChangeSortBy = (e) => {
        setSortBy(e.target.value);
        setPage(1);
    }

    // 20261007 검색어 입력 시 즉시 필터링, 첫 페이지/첫 페이지 블록으로 이동
    const onChangeSearchKeyword = (e) => {
        setSearchKeyword(e.target.value);
        setPage(1);
        setBlockNum(0);
    }

    // 20261007 검색어 초기화
    const onClearSearchKeyword = () => {
        setSearchKeyword("");
        setPage(1);
        setBlockNum(0);
    }

    // 260804 로컬 사이트 필터 -> 정렬 순서로 클라이언트에서 처리 (페이지네이션 이전 단계)
    const processedList_configLocalServer = useMemo(() => {
        let list = list_configLocalServer || [];

        if (siteFilterSeq !== -1) {
            list = list.filter(info => info.localSiteSeq === siteFilterSeq);
        }

        // 20261007 사이트 필터 다음 단계로 검색어 필터 적용
        if (searchKeyword.trim() !== "") {
            list = list.filter(info => isMatchLocalServerKeyword(info, searchKeyword));
        }

        list = list.filter(info => matchesLocalServerFilters(info, statusFilters));

        if (siteFilterSeq === -1 && sortBy === "site") {
            list.sort((a, b) => (a.localSiteSeq - b.localSiteSeq) || (a.localServerSeq - b.localServerSeq));
        } else {
            list.sort((a, b) => a.localServerSeq - b.localServerSeq);
        }

        return list;
    }, [list_configLocalServer, siteFilterSeq, sortBy, searchKeyword, statusFilters]);

    // 20261023 재조회나 토큰 초기화로 필터 결과가 줄어도 빈 마지막 페이지에 머물지 않는다.
    useEffect(() => {
        const lastPage = Math.max(1, Math.ceil(processedList_configLocalServer.length / limit));
        if (page > lastPage) { setPage(lastPage); setBlockNum(Math.floor((lastPage - 1) / 10)); }
    }, [processedList_configLocalServer.length, limit, page]);

    // 260804 로컬 서버 신규 등록 시, 새로 추가된 서버가 현재 사이트 필터에 가려져 있으면 필터를 해제해서 보이게 함
    const handledCreatedSeqRef = useRef(-1);

    useEffect(() => {
        if (
            lastCreatedLocalServerSeq !== undefined &&
            lastCreatedLocalServerSeq !== null &&
            lastCreatedLocalServerSeq !== -1 &&
            lastCreatedLocalServerSeq !== handledCreatedSeqRef.current
        ) {
            const newServer_info = (list_configLocalServer || []).find(info => info.localServerSeq === lastCreatedLocalServerSeq);

            if (newServer_info && siteFilterSeq !== -1 && siteFilterSeq !== newServer_info.localSiteSeq) {
                setSiteFilterSeq(-1);
            }

            // 20261007 새로 추가된 서버가 검색어에 가려지면 검색어 해제
            if (newServer_info && !isMatchLocalServerKeyword(newServer_info, searchKeyword)) {
                setSearchKeyword("");
            }
            if (newServer_info && !matchesLocalServerFilters(newServer_info, statusFilters)) {
                setStatusFilters({ ...defaultLocalServerFilters });
            }
        }
    }, [lastCreatedLocalServerSeq, list_configLocalServer]);

    // 260804 정렬/필터가 적용된 목록에서 새로 추가된 서버가 위치한 페이지로 자동 이동
    useEffect(() => {
        if (
            lastCreatedLocalServerSeq !== undefined &&
            lastCreatedLocalServerSeq !== null &&
            lastCreatedLocalServerSeq !== -1 &&
            lastCreatedLocalServerSeq !== handledCreatedSeqRef.current
        ) {
            const idx = processedList_configLocalServer.findIndex(info => info.localServerSeq === lastCreatedLocalServerSeq);

            if (idx !== -1) {
                setPage(Math.floor(idx / limit) + 1);
                handledCreatedSeqRef.current = lastCreatedLocalServerSeq;
            }
        }
    }, [lastCreatedLocalServerSeq, processedList_configLocalServer, limit]);

    // 260804 페이지 공통 결과 알림을 상단에 표시하고 약 2.5초 후 자동으로 숨긴다.
    const [lAlertKind, setLAlertKind] = useState(ConstData.ALERT_KIND.NONE);
    const [lAlertMessage, setLAlertMessage] = useState("");

    useEffect(() => {
        if (localServerAlertTarget !== "PAGE" || alertKind === ConstData.ALERT_KIND.NONE) {
            setLAlertKind(ConstData.ALERT_KIND.NONE);
            setLAlertMessage("");
            return;
        }
        setLAlertKind(alertKind);
        setLAlertMessage(alertMessage);
        const timer = setTimeout(() => {
            setLAlertKind(ConstData.ALERT_KIND.NONE);
            setLAlertMessage("");
        }, 2500);
        return () => clearTimeout(timer);
    }, [localServerAlertEventId, localServerAlertTarget, alertKind, alertMessage]);

    useEffect(() => {
        if (localServerAlertTarget !== "TYPE" || alertKind === ConstData.ALERT_KIND.NONE) {
            setTypeAlert({ kind: ConstData.ALERT_KIND.NONE, message: "" });
            return;
        }
        // 20260814 서버 타입 저장 알림은 타입 설정 패널 안에서만 반복 표시한다.
        setTypeAlert({ kind: alertKind, message: alertMessage });
        const timer = setTimeout(() => setTypeAlert({ kind: ConstData.ALERT_KIND.NONE, message: "" }), 2500);
        return () => clearTimeout(timer);
    }, [localServerAlertEventId, localServerAlertTarget, alertKind, alertMessage]);

    return (
        // teacher/class 탭과 동일한 app-container / main-title-bar / btn-top-register 스타일 적용
        // (기존 로직·핸들러·props는 그대로 유지, 마크업/className만 교체)
        <div className="web-main admin list-area">
            <div className="app-container">
                <div className="main-title-bar">
                    <div className="title-left">
                        <FaServer className="icon-teacher" aria-hidden="true" />
                        <span className="title-text">{t("로컬_서버", { ns: "admin" })}</span>
                        <button type="button" className="btn-top-register" onClick={toggleAddLocalServer}>
                            <FaPlusCircle aria-hidden="true" />{t("로컬_서버_등록", { ns: "admin" })}
                        </button>
                        {/* 20260814 서버 타입 설정은 필요할 때만 펼쳐 보도록 별도 버튼으로 분리한다. */}
                        <button type="button" className="btn-top-register" onClick={toggleLocalServerType}>
                            <FaCog aria-hidden="true" />{t("로컬_서버_타입_사용_설정", { ns: "admin" })}
                        </button>
                    </div>
                </div>
                {/* 260804 등록/삭제/상세설정 저장 결과를 상단에서 공통으로 표시 (약 2.5초 후 자동으로 사라짐) */}
                <div className="px-2">
                    <Alerts kind={lAlertKind} message={lAlertMessage} />
                </div>
                {/* 20260814 등록·타입 설정 패널을 검색 조건보다 위에 배치한다. */}
                <Collapse isOpen={isAddLocalServerOpen} onExited={resetAddLocalServer}>
                    <WebAddLocalServer
                        selectedSubDomainInfo={selectedSubDomainInfo}
                        list_localSite={list_localSite}
                        list_localServerType={list_localServerType}
                        isAddLocalServerOK={isAddLocalServerOK}
                        resetEventId={addLocalServerResetEventId}
                        alertKind={alertKind}
                        alertMessage={alertMessage}
                        localServerAlertEventId={localServerAlertEventId}
                        localServerAlertTarget={localServerAlertTarget}
                        onChangeIsAddLocalServerOK={onChangeIsAddLocalServerOK}
                        handleSelectedFunc={handleSelectedFunc}
                    />
                </Collapse>
                <Collapse isOpen={isLocalServerTypeOpen}>
                    <div className="d-flex py-3 align-items-center justify-content-center">
                        <div className="card web-list-card border-primary p-3">
                            {/* 20260814 타입별 카드 배열과 저장 버튼을 등록 패널과 같은 테두리 안에 표시한다. */}
                            {/* 20260828 마지막 행의 단일 카드가 전체 폭으로 늘어나지 않도록 3열 기본 폭을 고정한다. */}
                            <div className="d-flex flex-wrap" style={{ gap: '10px' }}>
                                {draftLocalServerTypes.map(info => {
                                    const isUnknown = info.localServerType === "UNKNOWN";
                                    const isLegacy = info.localServerType === "LEGACY_ODROID";
                                    const isFixed = isUnknown || isLegacy;
                                    return (
                                        <div key={info.localServerType} className="d-flex align-items-center justify-content-between hiclasstv-lightgrey round px-3 py-2" style={{ flex: '0 0 calc((100% - 20px) / 3)', minWidth: '230px' }}>
                                            <div className="d-flex flex-column mr-3">
                                                <span className="font-md hiclass-text"><b>{t(`서버_타입_${info.localServerType}`, { ns: "admin", defaultValue: info.localServerTypeName })}</b></span>
                                                {isFixed && <span className="font-sm grey-text">{t(isUnknown ? "서버_타입_기본값" : "서버_타입_신규_사용_안함", { ns: "admin" })}</span>}
                                            </div>
                                            {!isFixed &&
                                                // 20260814 슬라이더 클릭이 숨겨진 input에 전달되도록 label로 연결한다.
                                                <label className="hiclass-switch m-0">
                                                    <input type="checkbox" checked={info.useYn === "Y"} onChange={() => onToggleLocalServerTypeUse(info.localServerType)} />
                                                    <span className="hiclass-switch-slider" />
                                                </label>
                                            }
                                        </div>
                                    );
                                })}
                            </div>
                            <Alerts kind={typeAlert.kind} message={typeAlert.message} />
                            <div className="d-flex justify-content-center mt-3">
                                {/* 20260814 로컬 서버 생성 버튼과 같은 글자형 저장 버튼으로 표시한다. */}
                                <button type="button" className="btn btn-md hiclasstv-blue white-text m-0 px-4 py-1" onClick={onSaveLocalServerTypeUse} disabled={!hasLocalServerTypeChanges}>
                                    <b>{t("저장", { ns: "common" })}</b>
                                </button>
                            </div>
                        </div>
                    </div>
                </Collapse>
                <WebConfigSearchBoxExceptUser
                    adminTap={enAdminTap.LocalServer}
                    selectedSubDomainInfo={selectedSubDomainInfo}
                    list_configSubDomain={list_configSubDomain}
                    handleSearchBoxFunc={handleSelectedFunc}
                >
                    {/* 20261007 로컬 서버 검색 - 서브 도메인 오른쪽(정렬 위)에 배치, select와 같은 박스 스타일 */}
                    <div className="web-search-option">
                        <div className="web-search-select-label">
                            <span><b>{t("로컬_서버_검색", { ns: "admin" })}</b></span>
                        </div>
                        <div className="web-search-select-box">
                            <div className="local-server-search-wrap">
                                <input
                                    // 20261027 검색란을 로그인 아이디 자동완성 대상과 구분한다.
                                    type="search"
                                    id="local-server-search"
                                    name="localServerSearch"
                                    autoComplete="off"
                                    value={searchKeyword}
                                    className="local-server-search-input"
                                    placeholder={t("로컬_서버_검색_입력", { ns: "admin" })}
                                    onChange={onChangeSearchKeyword}
                                />
                                {
                                    searchKeyword !== "" ?
                                        <button type="button" className="local-server-search-icon clear" title={t("로컬_서버_검색_초기화", { ns: "admin" })} onClick={onClearSearchKeyword}>
                                            <FaTimes aria-hidden="true" />
                                        </button> :
                                        <span className="local-server-search-icon" aria-hidden="true">
                                            <FaSearch />
                                        </span>
                                }
                            </div>
                        </div>
                    </div>
                </WebConfigSearchBoxExceptUser>
                {/* 260804 로컬 사이트 필터 / 정렬 옵션 (요구사항 7) - 기존 web-search-box 스타일 재사용 */}
                <div className="px-2">
                    <div className="web-search-box">
                        <div className="web-search-option-area white" style={{ justifyContent: 'unset', flexWrap: 'wrap' }}>
                            <div className="web-search-option">
                                <div className="web-search-select-label">
                                    <span><b>{t("로컬_사이트", { ns: "admin" })}</b></span>
                                </div>
                                <div className="web-search-select-box">
                                    <select className="custom-select small my-1" style={{ width: '100%', fontSize: '0.9rem' }} value={siteFilterSeq} onChange={onChangeSiteFilter}>
                                        <option value={-1}>{t("전체", { ns: "admin" })}</option>
                                        {
                                            list_localSite && list_localSite.length > 0 &&
                                                list_localSite.map((info) => (
                                                    <option value={info.localSiteSeq} key={"siteFilterOption-" + info.localSiteSeq}>{info.localSiteName}</option>
                                                ))
                                        }
                                    </select>
                                </div>
                            </div>
                            <div className="web-search-option">
                                <div className="web-search-select-label">
                                    <span><b>{t("정렬", { ns: "admin" })}</b></span>
                                </div>
                                <div className="web-search-select-box">
                                    <select className="custom-select small my-1" style={{ width: '100%', fontSize: '0.9rem' }} value={sortBy} onChange={onChangeSortBy} disabled={siteFilterSeq !== -1}>
                                        <option value="site">{t("사이트별_그룹", { ns: "admin" })}</option>
                                        <option value="seq">{t("서버_번호순", { ns: "admin" })}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* 20261023 검색·정렬은 유지하고 페이지 나누기 전에 선택형 상태 필터를 적용한다. */}
                <div className="mx-2 my-2 p-3" style={{ background: '#f6f7f9', borderRadius: '6px' }}>
                    <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '8px' }}>
                        <button type="button" aria-expanded={isStatusFilterOpen} aria-controls="local-server-status-filters" onClick={() => setIsStatusFilterOpen(open => !open)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: 0, minHeight: '32px', border: 0, background: 'transparent', color: 'inherit', fontSize: 'inherit', cursor: 'pointer' }}>
                            <b>{t("서버_상태_필터", { ns: "admin" })}</b>
                            <span className="font-sm">{t(isStatusFilterOpen ? "필터_접기" : "필터_펼치기", { ns: "admin" })}</span>
                            {isStatusFilterOpen ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
                        </button>
                        {tokenResetServerSeq !== null && <div role="status" className="text-success">#{tokenResetServerSeq} {t("보고_토큰_초기화_완료", { ns: "admin" })}</div>}
                        <span aria-live="polite">{t("서버_필터_조회_건수", { ns: "admin", total: (list_configLocalServer || []).length, count: processedList_configLocalServer.length })}</span>
                    </div>
                    <Collapse isOpen={isStatusFilterOpen} id="local-server-status-filters">
                    <div className="d-flex flex-wrap pt-2" style={{ gap: '12px' }}>
                        {statusFilterDefinitions.map(([key, label, options]) => (
                            <label key={key} style={{ flex: '1 1 220px', minWidth: 0, margin: 0 }}>
                                <span className="font-sm">{t(label, { ns: "admin" })}</span>
                                <select className="custom-select small mt-1" style={{ width: '100%', fontSize: '0.9rem' }} value={statusFilters[key]} onChange={event => changeStatusFilters({ [key]: event.target.value })}>
                                    {options.map(([value, text]) => <option key={value} value={value}>{filterOptionLabel(value, text)}</option>)}
                                </select>
                            </label>
                        ))}
                    </div>
                    <div className="d-flex flex-wrap mt-2" style={{ gap: '8px' }}>
                        <button type="button" style={quickFilterStyle({ update: "allowed", token: "unissued" })} aria-pressed={isQuickFilterSelected({ update: "allowed", token: "unissued" })} onClick={() => changeStatusFilters({ ...defaultLocalServerFilters, update: "allowed", token: "unissued" })}>{t("필터_허용_미발급", { ns: "admin" })}</button>
                        <button type="button" style={quickFilterStyle({ update: "allowed", status: "FAILED" })} aria-pressed={isQuickFilterSelected({ update: "allowed", status: "FAILED" })} onClick={() => changeStatusFilters({ ...defaultLocalServerFilters, update: "allowed", status: "FAILED" })}>{t("필터_허용_실패", { ns: "admin" })}</button>
                        <button type="button" style={quickFilterButtonStyle} onClick={() => { changeStatusFilters(defaultLocalServerFilters); setSearchKeyword(""); setSiteFilterSeq(-1); }}>{t("필터_전체_초기화", { ns: "admin" })}</button>
                    </div>
                    </Collapse>
                </div>
                {/* 20261023 현재 적용된 상태 조건을 미리보기와 같은 연한 파란 배지로 표시한다. */}
                <div className="d-flex flex-wrap mx-2 mb-2" aria-live="polite" style={{ gap: '8px' }}>
                    {statusFilterDefinitions.filter(([key]) => statusFilters[key] !== "all").map(([key, label, options]) => {
                        const selected = options.find(([value]) => value === statusFilters[key]);
                        return selected ? <span key={key} style={{ background: '#edf4ff', color: '#2869e8', borderRadius: '4px', padding: '4px 8px', fontSize: '0.8rem', lineHeight: 1.5 }}>{t(label, { ns: "admin" })}: {filterOptionLabel(selected[0], selected[1])}</span> : null;
                    })}
                </div>
                <WebPagination
                    total={processedList_configLocalServer.length <= 0 ? 1 : processedList_configLocalServer.length}
                    limit={limit}
                    setLimit={setLimit}
                    page={page}
                    setPage={setPage}
                    blockNum={blockNum}
                    setBlockNum={setBlockNum}
                />
                <WebLocalServerList
                    currentPage={page}
                    list_localSite={list_localSite}
                    list_localServerType={list_localServerType}
                    // 260804 로컬 사이트 필터 / 정렬 적용된 목록을 페이지네이션
                    list_localServer={processedList_configLocalServer.slice(offset, offset + limit)}
                    // 20261007 검색 중 결과가 없을 때 별도 문구 표시용
                    isSearching={searchKeyword.trim() !== "" || Object.keys(defaultLocalServerFilters).some(key => statusFilters[key] !== "all")}
                    handleSelectedFunc={handleLocalServerSelectedFunc}
                />
                {tokenResetTarget && <WebReportTokenReset
                    server={tokenResetTarget}
                    onClose={() => setTokenResetTarget(null)}
                    handleSelectedFunc={handleLocalServerSelectedFunc}
                />}
            </div>
        </div>
    )
}

export default WebLocalServerConfig;
